import { Link, Outlet, useLocation } from 'react-router-dom';

/**
 * Settings → Safety. One workspace, one tab per former Safety settings page.
 *
 * Each tab is its own route (`/settings/safety/metrics`, `/settings/safety/forms`)
 * so a tab can be linked to, and so the URLs the two pages had before this
 * workspace existed still land on the right tab rather than 404ing. The form
 * builder, upload and extraction-review screens under `/settings/safety/forms/*`
 * stay full-screen outside the settings layout; their exits navigate to
 * `/settings/safety/forms`, which is the Forms tab.
 *
 * No back arrow: the settings sidebar is the way out on desktop, and the
 * layout's own back strip is the way out on mobile. A third would be noise.
 */
const TABS = [
  { label: 'Metrics', to: '/settings/safety/metrics' },
  { label: 'Forms', to: '/settings/safety/forms' },
] as const;

export default function SafetyWorkspace() {
  const { pathname } = useLocation();

  return (
    <div className="min-h-full">
      <div className="border-b border-border px-6 py-3">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-foreground">Safety</h1>
        </div>
      </div>

      <div className="px-6 pt-4">
        <div className="flex items-center gap-1 border-b border-border" role="tablist" aria-label="Safety settings">
          {TABS.map((t) => {
            const active = pathname === t.to || pathname === `${t.to}/`;
            return (
              <Link
                key={t.to}
                to={t.to}
                role="tab"
                aria-selected={active}
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  active
                    ? 'border-fieldiq-accent text-fieldiq-accent'
                    : 'border-transparent text-fieldiq-muted hover:text-fieldiq-text'
                }`}
              >
                {t.label}
              </Link>
            );
          })}
        </div>
      </div>

      <Outlet />
    </div>
  );
}
